import { useUserInfo } from "@next-platform/client-common-core";
import {
  getRulebookDraftOwner,
  setRulebookDraftOwner,
  subscribeRulebookDrafts,
} from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import { useLayoutEffect, useRef, useSyncExternalStore } from "react";

/**
 * Scopes the draft store to the signed-in user before the Editor reads any draft, so each account on a shared device
 * only sees its own drafts (signed-out drafts stay in memory). Returns whether the store is scoped to `userId` yet.
 * While the profile is still loading the identity of `userId` is untrusted (it may already be the signed-out
 * fallback), so the store is left untouched and the hook reports not-ready — scoping to `null` and then re-scoping
 * would drop the previous owner's drafts before the real id arrives. Once a real owner is seen it stays pinned:
 * a silent session expiry flips `userId` to `null` while the editor is open, and rescoping to anonymous would
 * unmount the editor and strand later edits, so only a different non-null `userId` rescopes.
 */
export default function useRulebookDraftOwner(userId: string | null | undefined): boolean {
  const { isAuthenticated, isProfileLoaded } = useUserInfo();
  const isProfilePending = isAuthenticated && !isProfileLoaded;
  const refPinnedOwner = useRef<string | null>(null);
  const owner = userId ? userId : refPinnedOwner.current;
  useLayoutEffect(() => {
    if (isProfilePending) {
      return;
    }
    if (userId) {
      refPinnedOwner.current = userId;
    }
    setRulebookDraftOwner(owner);
  }, [userId, owner, isProfilePending]);
  const isScoped = useSyncExternalStore(subscribeRulebookDrafts, () => getRulebookDraftOwner() === owner);
  return !isProfilePending && isScoped;
}
